import { AppDispatch } from '../store';
import { loginStart, loginSuccess, loginFailure, logout as logoutAction, updateUser } from '../reducers/user.reducer';
import * as userApi from '../../api/userApi';
import { User } from '../../types';

interface LoginData {
  email: string;
  password: string;
}

interface SignUpData {
  email: string;
  password: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
}

export const login = (data: LoginData) => {
  return async (dispatch: AppDispatch) => {
    dispatch(loginStart());
    try {
      const response = await userApi.login(data);
      if (response.data && response.data.data && response.data.data.user) {
        dispatch(loginSuccess(response.data.data.user));
        return { success: true, data: response.data.data };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      dispatch(loginFailure(errorMessage));
      throw error;
    }
  };
};

export const signUp = (data: SignUpData) => {
  return async (dispatch: AppDispatch) => {
    dispatch(loginStart());
    try {
      const response = await userApi.signUp(data);
      // New members require admin approval — no user/token returned
      if (response.data && response.data.pendingApproval) {
        dispatch(loginFailure('')); // clear loading state without setting an error
        return { success: true, pendingApproval: true, message: response.data.message };
      }
      if (response.data && response.data.data && response.data.data.user) {
        dispatch(loginSuccess(response.data.data.user));
        return { success: true, data: response.data.data };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Sign up failed';
      dispatch(loginFailure(errorMessage));
      throw error;
    }
  };
};

export const getUserInfo = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await userApi.getCurrentUser();
      if (response.data && response.data.data && response.data.data.user) {
        dispatch(loginSuccess(response.data.data.user));
        return response.data.data.user;
      }
    } catch (error: any) {
      // Only logout on 401 (unauthorized), not on other errors
      if (error.response?.status === 401) {
        dispatch(logoutAction());
      }
      console.error('getUserInfo error:', error.message);
    }
  };
};

export const logout = () => {
  return async (dispatch: AppDispatch) => {
    try {
      await userApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch(logoutAction());
    }
  };
};

export const updateUserProfile = (updates: Partial<User>) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await userApi.updateProfile(updates);
      if (response.data && response.data.data && response.data.data.user) {
        dispatch(updateUser(response.data.data.user));
        return response.data.data.user;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Update failed';
      dispatch(loginFailure(errorMessage));
      throw error;
    }
  };
};